"use client";

import React, { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import { ProductZoom } from "./product-zoom";
import { useTranslations } from "next-intl";
import axios from "axios";

const mainImageSize = 520;

const getImagesForProduct = (product) => {
	if (!product?.metadata) return [];
	return JSON.parse(product.media);
};

function ImageGallerySwiper({ product }) {
	const imagesList = getImagesForProduct(product) as any[];

	const [active, setActive] = useState(0);
	const [mainImage, setMainImage] = useState<any>("");
	const [videoUrl, setvideoUrl] = useState<any>("");
	const [images, setImages] = useState<any[]>([]);
	const t = useTranslations();

	const changeProductImages = (product) => {
		if (product === null) {
			setImages([]);
			setMainImage("");
			setActive(0);
			return;
		}
	};

	useEffect(() => {
		if (product) {
			changeProductImages(product);
			setActive(0);
			getvideo();
		}
	}, [product]);

	async function getvideo() {
		let coverMp4Obj = JSON.parse(
			product.metadata.filter((item) => item.key == "cover_mp4")[0].value,
		)[0];
		if (coverMp4Obj) {
			let { data } = await axios(
				`${process.env.NEXT_PUBLIC_LANGUAGE_LIST_URL}/medias/video/${coverMp4Obj.id}`,
			);
			setvideoUrl(data.detail.video_url);

			if (imagesList[0] && imagesList[0].url) {
				setMainImage("");
				setImages(imagesList);
			}
		} else {
			if (imagesList[0] && imagesList[0].url) {
				setMainImage(imagesList[0].url);
				setImages(imagesList);
			}
		}
	}

	const setActiveImgIndex = (index: number) => {
		if (index >= 0 && index < images.length) {
			setActive(index);
			setMainImage(images[index].url);
		}
	};

	const threeModel = useMemo<any>(() => {
		const json = product.metadata.find((item) => item.key === "threeModel");
		return json ? JSON.parse(json.value)[0] : null;
	}, [product]);

	const thumbContainerRef = React.useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (thumbContainerRef.current && images.length > 0) {
			const activeThumb = thumbContainerRef.current.children[active] as HTMLElement;
			if (activeThumb) {
				activeThumb.scrollIntoView({
					behavior: "smooth",
					block: "nearest",
				});
			}
		}
	}, [active, images.length]);

	return (
		<div className="flex w-full select-none gap-3 max-md:flex-col-reverse md:items-stretch md:gap-4">
			{/* Thumbnail strip */}
			{images.length > 1 && (
				<div className="flex shrink-0 max-md:w-full md:w-[72px] lg:w-[80px]">
					<div
						ref={thumbContainerRef}
						className="flex gap-2 max-md:w-full max-md:overflow-x-auto max-md:pb-1 md:flex-col md:overflow-y-auto"
						style={{
							scrollbarWidth: "none",
							msOverflowStyle: "none",
						}}
					>
						{images.map((image: any, index: number) => (
							<button
								key={index}
								type="button"
								className={`group relative shrink-0 overflow-hidden rounded-md transition-all duration-200 max-md:h-[60px] max-md:w-[60px] md:aspect-square md:w-full ${
									active === index
										? "ring-2 ring-brand-ink ring-offset-1"
										: "ring-1 ring-zinc-200 hover:ring-zinc-400"
								}`}
								onMouseEnter={() => {
									setMainImage(image.url);
									setActive(index);
								}}
								onClick={() => {
									setMainImage(image.url);
									setActive(index);
								}}
							>
								<Image
									quality={80}
									src={image.url}
									width={80}
									height={80}
									alt={`Product view ${index + 1}`}
									className="h-full w-full object-cover"
								/>
							</button>
						))}
					</div>
				</div>
			)}

			{/* Main display area */}
			<div className="relative flex flex-1 items-center justify-center overflow-hidden rounded-lg bg-[oklch(97.5%_0.005_72)] max-md:aspect-square md:aspect-square">
				{mainImage ? (
					<div className="w-full">
						<ProductZoom
							src={mainImage}
							imageWidth={mainImageSize}
							imageHeight={mainImageSize}
							setActiveImgIndex={setActiveImgIndex}
							quality={100}
							imgList={images}
							fit="contain"
						/>
					</div>
				) : videoUrl ? (
					<video
						src={videoUrl}
						poster="/image/default-image.webp"
						autoPlay
						loop
						muted
						playsInline
						className="aspect-square w-full max-w-[520px] rounded-md object-cover"
					/>
				) : null}

				{/* Image counter */}
				{images.length > 1 && (
					<span className="absolute bottom-3 right-3 z-20 rounded-full bg-brand-ink/70 px-2.5 py-0.5 font-body text-[11px] tabular-nums text-white backdrop-blur-sm">
						{active + 1} / {images.length}
					</span>
				)}
			</div>
		</div>
	);
}

export default React.memo(ImageGallerySwiper);
